import type { StackProps } from 'smoothie';

export const MAX_NUMBER_PHYSICAL_CARDS = 1;
export const CARD_ASPECT_RATIO = 1.58;
export const CARDHOLDER_NAME_MAX_LENGTH = 21;

export enum CardForm {
    PHYSICAL = 'PHYSICAL',
    VIRTUAL = 'VIRTUAL',
}

export enum CardType {
    CREDIT = 'CREDIT',
    DEBIT = 'DEBIT',
    PREPAID = 'PREPAID',
}

export enum CardColor {
    GREEN = 'green',
    PINK = 'pink',
    YELLOW = 'yellow',
    PEACH = 'peach',
    BLUE = 'blue',
    VIOLET = 'violet',
}

export enum CardStatus {
    ACTIVE = 'ACTIVE',
    PENDING = 'PENDING',
    REJECTED = 'REJECTED',
    SUSPENDED = 'SUSPENDED',
    TERMINATED = 'TERMINATED',
    UNACTIVATED = 'UNACTIVATED',
}

export enum UserCardStatusReason {
    REQ = 'REQ', // requested by you
    NEW = 'NEW', // card activated
    SUP = 'SUP', // contact support for more details
}

export enum CardFulfillmentStatus {
    DIGITALLY_PRESENTED = 'DIGITALLY_PRESENTED',
    ISSUED = 'ISSUED',
    ORDERED = 'ORDERED',
    REISSUED = 'REISSUED',
    REJECTED = 'REJECTED',
    REORDERED = 'REORDERED',
    SHIPPED = 'SHIPPED',
}

export enum UpdateStatus {
    ACTIVE = 'ACTIVE',
    SUSPENDED = 'SUSPENDED',
    TERMINATED = 'TERMINATED',
}

export enum UpdateReason {
    REQ = 'REQ', // requested by you
}

export enum UserReissueReason {
    Stolen = 'STOLEN',
    Lost = 'LOST',
    Damaged = 'DAMAGED',
    Appearance = 'APPEARANCE',
}

export enum CardWidgetType {
    ACTIVATE_CARD = 'ACTIVATE_CARD',
    SET_PIN = 'SET_PIN',
}

export interface CardMailingAddress {
    line1: string;
    line2?: string;
    city: string;
    postalCode: string;
    state: string;
    countryCode: string;
}

export interface CardShipping {
    recipientFirstName: string;
    recipientLastName: string;
    phoneNumber: string;
    address: CardMailingAddress;
}

export interface IssueCardBody {
    form: CardForm;
    embossName: string;
    syncteraAccountId: string;
    cardColor?: CardColor;
    shipping?: CardShipping;
}

export interface ReissueCardBody extends IssueCardBody {
    reissuedFromId: string;
    reissueReason: UserReissueReason;
}

export interface CardFulfillmentDetails {
    shipDate?: string;
    shippingMethod?: string;
    trackingNumber?: string;
}

export interface EmbossName {
    line1: string;
    line2: string;
}

export interface Card {
    id: string;
    syncteraAccountId: string;
    syncteraPersonId: string;
    creationTime: string;
    embossName: EmbossName;
    type: CardType;
    form: CardForm;
    color?: CardColor;
    cardStatus: CardStatus;
    userCardStatusReason: UserCardStatusReason;
    expirationMonth?: string;
    expirationTime?: string;
    expirationYear?: string;
    isPinSet?: boolean;
    lastFour?: string;
    bin?: string;
    cardBrand: string;
    shipping?: Partial<CardShipping> & {
        address?: Partial<CardMailingAddress>;
    };
    cardFulfillmentStatus?: CardFulfillmentStatus;
    fulfillmentDetails?: CardFulfillmentDetails;
    reissuedToId?: string;
}

export interface CardWidgetUrlRequest {
    syncteraAccountId: string; // Account Id directly tied to the card (i.e. charge secured account)
    widgetType: CardWidgetType;
    cardId?: string;
}

export interface CardWidgetProps {
    url: string;
}

export interface CardDetailsProps {
    cardId: string;
    width: number;
}

export enum CardDataType {
    PAN = 'pan',
    CVV = 'cvv',
    CVV11 = 'cvv11',
    EXP = 'exp',
    EXP11 = 'exp11',
    COPY_PAN = 'copy_pan',
    COPY_CVV = 'copy_cvv',
    COPY_EXP = 'copy_exp',
}

export enum CardPaymentStatus {
    AutoPayEnabled = 'autoPayEnabled',
    NoManualPayment = 'noManualPayment',
    UpcomingManualPayment = 'upcomingManualPayment',
    OverdueManualPayment = 'overdueManualPayment',
}

export interface CardDataProps extends StackProps {
    cardDataType: CardDataType;
    cardId: string;
    onCopy?: () => void;
}

export interface AddToAppleWalletButtonProps {
    card: Pick<Card, 'embossName' | 'id' | 'lastFour'>;
    onSuccess: () => void;
    onError: () => void;
    onProgress: () => void;
}
